import { generateGuid } from 'utils/Guid';
import { produce, current, castDraft, setAutoFreeze, isDraft } from 'immer';
import { WritableDraft } from 'immer/dist/internal';
import { useEffect, useMemo } from 'react';
import { QueueUtil } from 'utils/QueueUtils';
import { isNullOrWhitespace } from 'utils/StringUtils';
import create, { UseBoundStore, StoreApi } from 'zustand';
import { ITreeNode } from './CommonTreeNode';

export interface ITreeStore<T> {
  nodes: ITreeNode<T>[];
  isLoading: boolean;

  /**
   * Use when actions sequence is important
   * for example to syncronize events rapid fire (signalR events from back-end)
   *
   * @example
   *
   * // for react:
   * const queue = useTreeStore(s => s.queue);
   * queue.enqueue({callback: promise1})
   * // ...
   * queue.enqueue({callback: promise2}) // promise 2 will be allways done after promise1
   *
   * // or for mvc:
   *
   * dpComponents.getTreeStore(this.treeId).queue.enqueue({callback: promise });
   * // ...
   * dpComponents.getTreeStore(this.treeId).queue.enqueue({callback: promise }); // promise 2 will be allways done after promise1
   */
  queue: QueueUtil;
  enqueue: (callback: (store: ITreeStore<T>) => Promise<void>) => Promise<void>;
  treeId?: string;
  actions: {
    // toDo: add enqueue: action
    initialize: () => Promise<void>;

    toggleExpand: (node: string | ITreeNode<T>, isExpanded: boolean) => Promise<void>;
    expandNode: (node: string | ITreeNode<T>) => Promise<void>;
    foldNode: (node: string | ITreeNode<T>) => Promise<void>;

    toggleSelect: (node: string | ITreeNode<T>, isExpanded: boolean) => Promise<ITreeNode<T> | null>;
    selectNode: (node: string | ITreeNode<T>) => Promise<ITreeNode<T> | null>;
    unselectNode: (node: string | ITreeNode<T>) => Promise<ITreeNode<T> | null>;
    unselectNodes: () => Promise<void>;

    findNode: (id: string) => ITreeNode<T> | null;
    findNodes: (filter: (node: ITreeNode<T> | WritableDraft<ITreeNode<T>>) => boolean) => ITreeNode<T>[];

    /**
     * Updating node
     */
    updateNode: (node: ITreeNode<T>) => void;
    /**
     * Updating nodes list
     */
    updateNodes: (nodes: ITreeNode<T>[]) => void;

    /**
     * reloading node via provided function `reloadNode` to state manager props
     */
    reloadNode: (node: string | ITreeNode<T>, subFolder?: T) => Promise<void>;
    setChildNodes: (nodeId: string, childNodes: ITreeNode<T>[]) => Promise<void>;
    loadNodes: (node: string | ITreeNode<T>, force?: boolean) => Promise<ITreeNode<T>[]>;
    /**
     * reloading tree via provided function `loadTree` to state manager props
     */
    loadTree: () => Promise<void>;

    setNodeLoading: (nodeId: string, isLoading: boolean) => void;

    /**
     * Loading and selecting node deep in the tree by path
     */
    navigateToTheDeep: (nodeId: string, pathIds: string[]) => Promise<ITreeNode<T> | null | undefined>;

    deleteNode: (node: string | ITreeNode<T>) => Promise<void>;
    pushNode: (node: ITreeNode<T>, parent?: string | ITreeNode<T>) => Promise<void>;
    unshiftNode: (node: ITreeNode<T>, parent?: string | ITreeNode<T>) => Promise<void>;
    //deleteNodes
    replaceNodes: (nodes: ITreeNode<T>[]) => void;
  };
}

export interface ITreeStoreProps<T> {
  nodes: ITreeNode<T>[];
  treeId: string;
  multiSelect?: boolean;
  /**
   * Do not uncache store, when component unmounted
   */
  doNotUncache?: boolean;
  /**
   * Loading childNodes for parent. Reloading whole tree if parent not provided
   */
  loadNodes?: (parent?: ITreeNode<T>) => Promise<ITreeNode<T>[]> | ITreeNode<T>[];
  onNodeExpanded?: (node: ITreeNode<T>, state: ITreeStore<T>) => Promise<void> | void;
  onNodeFolded?: (node: ITreeNode<T>, state: ITreeStore<T>) => Promise<void> | void;
  onNodeSelected?: (node: ITreeNode<T>, state: ITreeStore<T>) => Promise<void> | void;
  onNodeUnSelect?: (node: ITreeNode<T>, state: ITreeStore<T>) => Promise<void> | void;
  onNodeDeleted?: (
    deletedNode: string | ITreeNode<T>,
    parentNodeId: string | undefined,
    state: ITreeStore<T>
  ) => Promise<void> | void;
  onTreeLoaded?: (state: ITreeStore<T>) => Promise<void> | void;
  reloadNode?: (node: ITreeNode<T>, treeNode?: T) => Promise<ITreeNode<T>> | ITreeNode<T>;
  sortNodes?: (nodes: ITreeNode<T>[]) => ITreeNode<T>[];
  /**
   * Folder Tree use SelectNode event that need to load node if it not found
   */
  onNodeNotFound?: (node: string | ITreeNode<T>, type: NodeNotFoundType) => Promise<void> | void;
  /**
   * `true` to wait for tree caret rotation/open animation before triggering `onNodeExpanded`
   *
   * defaults to `true`
   */
  waitForCaretAnimationOnExpand?: boolean;
}

/**
 * Node might be not found for next actions, but callback is one
 */
export enum NodeNotFoundType {
  Expand,
  Fold,
  Select,
  Unselect,
  Delete,
  Reload,
}

const TreeStoreModelMap = new Map<string, UseBoundStore<StoreApi<ITreeStore<any>>>>();

export function getTreeStore<T>(uniqueId: string): ITreeStore<T> | undefined {
  return TreeStoreModelMap.get(uniqueId)?.getState() as ITreeStore<T>;
}

export function getBoundTreeStore<T>(uniqueId: string) {
  return TreeStoreModelMap.get(uniqueId) as UseBoundStore<StoreApi<ITreeStore<T>>>;
}

export function useTreeStore<T>(uniqueId: string) {
  return TreeStoreModelMap.get(uniqueId) as UseBoundStore<StoreApi<ITreeStore<T>>>;
}

export function unCacheStore(uniqueId: string) {
  TreeStoreModelMap.delete(uniqueId);
}

export function unCacheAllStores() {
  TreeStoreModelMap.clear();
}

export function createTreeStore<T>({
  treeId,
  nodes,
  multiSelect,
  loadNodes,
  onNodeExpanded,
  onNodeFolded,
  onNodeSelected,
  onNodeUnSelect,
  reloadNode,
  onTreeLoaded,
  sortNodes,
  onNodeDeleted,
  onNodeNotFound,
  waitForCaretAnimationOnExpand = true,
}: ITreeStoreProps<T>) {
  setAutoFreeze(false);
  const useStore = create<ITreeStore<T>>((set, get) => {
    const sortAndLoadNodes = async (parent?: ITreeNode<T>): Promise<ITreeNode<T>[]> => {
      if (loadNodes) {
        let nodes = await loadNodes(parent);
        if (sortNodes) {
          nodes = sortNodes(nodes);
        }
        return nodes;
      }

      return [];
    };

    const sortChildren = (node: ITreeNode<T> | WritableDraft<ITreeNode<T>>) => {
      if (sortNodes && node.childNodes?.length) {
        if (isDraft(node)) {
          node.childNodes = castDraft(sortNodes((current(node) as ITreeNode<T>).childNodes ?? []));
        } else {
          node.childNodes = sortNodes(node.childNodes as ITreeNode<T>[]);
        }
      }
    };

    const sortNodesOnCurrentLevel = (
      node: ITreeNode<T> | WritableDraft<ITreeNode<T>>,
      store: WritableDraft<ITreeStore<T>>
    ) => {
      if (!sortNodes) {
        return;
      }
      const parentNode = findNodesList(
        n => (n as ITreeNode<T>).childNodes?.filter(ch => ch.nodeId === node.nodeId).length === 1,
        store.nodes
      )[0] as ITreeNode<T>;
      if (parentNode?.childNodes?.length) {
        sortChildren(parentNode);
      } else {
        sortRoot(store);
      }
    };

    const sortRoot = (store: ITreeStore<T> | WritableDraft<ITreeStore<T>>) => {
      if (sortNodes && store.nodes?.length) {
        if (isDraft(store)) {
          store.nodes = castDraft(sortNodes((current(store) as ITreeStore<T>).nodes ?? []));
        } else {
          store.nodes = sortNodes((store.nodes as ITreeNode<T>[]) ?? []);
        }
      }
    };

    const isNeedToLoadNodes = (node: ITreeNode<T>, force?: boolean) => {
      return (
        loadNodes &&
        node?.hasChildren &&
        !node?.isLoading &&
        (!node?.childNodes?.length || node.alwaysLoadChildren || force)
      );
    };

    const unselectNodes = async (exceptNodeId?: string) => {
      const unselectedNodes: ITreeNode<T>[] = [];
      set(
        produce<ITreeStore<T>>(state => {
          const nodes = findNodesList(n => !!n.isActive && n.nodeId !== exceptNodeId, state.nodes);

          for (const node of nodes) {
            if (node.isActive !== false) {
              node.isActive = false;
              unselectedNodes.push(current(node) as ITreeNode<T>);
            }
          }
        })
      );

      if (unselectedNodes.length) {
        const loadings = unselectedNodes.map(node => onNodeUnSelect?.(node, get()));
        await Promise.all(loadings);
      }
    };

    const afterSelectedNodePush = async (node: ITreeNode<T>) => {
      !multiSelect && (await unselectNodes(node?.nodeId));
      await onNodeSelected?.(node, get());
    };

    return {
      treeId,
      nodes,
      queue: new QueueUtil(),
      enqueue: async (callback: (store: ITreeStore<T>) => Promise<void>) => {
        const store = get();
        return store.queue.enqueue({ callback: () => callback(store) });
      },
      isLoading: nodes.length === 0,
      multiSelect,
      actions: {
        initialize: async () => {
          get().queue.enqueue({
            callback: async () => {
              if (!get().nodes.length) {
                await get().actions.loadTree();
              }

              const expandedLeafs = findNodesList(n => n.isExpanded && !n.childNodes?.length, get().nodes) as ITreeNode<
                T
              >[];

              const loadings = expandedLeafs.map(leaf => get().actions.loadNodes(leaf));
              await Promise.all(loadings);
            },
          });
        },
        toggleExpand: async (node: string | ITreeNode<T>, isExpanded: boolean) => {
          if (isExpanded) {
            get().actions.expandNode(node);
          } else {
            get().actions.foldNode(node);
          }
        },
        expandNode: async (node: string | ITreeNode<T>) => {
          let updatedNode: ITreeNode<T> | undefined;
          set(
            updateNodeIfExist(node, node => {
              if (node.hasChildren) {
                node.isExpanded = true;
                updatedNode = current(node) as ITreeNode<T>;
                node.isLoading = isNeedToLoadNodes(updatedNode);
              }
            })
          );

          if (updatedNode) {
            await get().actions.loadNodes(updatedNode);
            if (onNodeExpanded) {
              if (waitForCaretAnimationOnExpand) {
                // 300ms transition animation
                // for example resize panel after expand
                setTimeout(() => onNodeExpanded(updatedNode!, get()), 300);
              } else {
                onNodeExpanded(updatedNode!, get());
              }
            }
          }
        },
        foldNode: async (node: string | ITreeNode<T>) => {
          let updatedNode: ITreeNode<T> | undefined;
          set(
            updateNodeIfExist(node, node => {
              if (node.hasChildren) {
                node.isExpanded = false;
                updatedNode = current(node) as ITreeNode<T>;
              }
            })
          );

          if (updatedNode && onNodeFolded) {
            if (waitForCaretAnimationOnExpand) {
              setTimeout(() => {
                // 300ms transition animation
                // for example resize panel after expand
                updatedNode && onNodeFolded(updatedNode, get());
              }, 300);
            } else {
              // callback immediately:
              updatedNode && onNodeFolded(updatedNode, get());
            }
          }
        },
        toggleSelect: async (node: string | ITreeNode<T>, isSelected: boolean) => {
          if (isSelected) {
            return get().actions.selectNode(node);
          } else {
            return get().actions.unselectNode(node);
          }
        },
        // ToDo: to complex - need simplify
        selectNode: async (node: string | ITreeNode<T>) => {
          if (!multiSelect) {
            // we don't want to reselect already selected node
            await unselectNodes(typeof node === 'string' ? node : node?.nodeId);
          }

          let currentNode: WritableDraft<ITreeNode<T>> | null = null;
          let nodeNotFound = true;
          set(
            updateNodeIfExist(node, node => {
              nodeNotFound = false;
              // we don't want to reselect already selected node
              if (node.isActive !== true) {
                node.isActive = true;
                currentNode = current(node);
              }
            })
          );

          if (currentNode) {
            await onNodeSelected?.(currentNode, get());
          } else if (nodeNotFound) {
            await onNodeNotFound?.(node, NodeNotFoundType.Select);
          }

          return currentNode;
        },
        unselectNode: async (node: string | ITreeNode<T>) => {
          let currentNode: WritableDraft<ITreeNode<T>> | null = null;
          set(
            updateNodeIfExist(node, node => {
              if (node.isActive !== false) {
                node.isActive = false;
                currentNode = current(node);
              }
            })
          );

          if (currentNode) {
            await onNodeUnSelect?.(currentNode, get());
          }

          return currentNode;
        },
        unselectNodes: async () => {
          await unselectNodes();
        },
        findNode: (id: string) => {
          return getNode(id, get().nodes) as ITreeNode<T>;
        },
        findNodes: (filter: (node: ITreeNode<T> | WritableDraft<ITreeNode<T>>) => boolean) => {
          return findNodesList(filter, get().nodes) as ITreeNode<T>[];
        },
        updateNode: (newNode: ITreeNode<T>) => {
          set(
            produce<ITreeStore<T>>(get(), draft => {
              sortChildren(newNode);
              setNode(newNode, draft.nodes);
              sortNodesOnCurrentLevel(newNode, draft);
            })
          );
        },
        updateNodes: (newNodes: ITreeNode<T>[]) => {
          set(
            produce<ITreeStore<T>>(get(), draft => {
              newNodes.forEach(newNode => {
                setNode(newNode, draft.nodes);
                sortNodesOnCurrentLevel(newNode, draft);
              });
            })
          );
        },
        deleteNode: async (node: string | ITreeNode<T>) => {
          let parentNode: ITreeNode<T> | undefined;
          if (onNodeDeleted) {
            const nodeId = typeof node === 'string' ? node : node?.nodeId;
            parentNode = findNodesList(
              n => (n as ITreeNode<T>).childNodes?.filter(ch => ch.nodeId === nodeId).length === 1,
              get().nodes
            )[0] as ITreeNode<T>;
          }

          let deleted = false;
          set(
            produce<ITreeStore<T>>(get(), draft => {
              deleted = deleteNode(node, draft.nodes);
            })
          );

          if (deleted) {
            await onNodeDeleted?.(node, parentNode?.nodeId, get());
          }
        },
        pushNode: async (node: ITreeNode<T>, parent?: string | ITreeNode<T>) => {
          let isPushed;
          set(
            produce<ITreeStore<T>>(state => {
              const parentNode =
                typeof parent === 'string'
                  ? (getNode(parent, state.nodes) as WritableDraft<ITreeNode<T>>)
                  : (parent as WritableDraft<ITreeNode<T>>);
              if (parentNode) {
                !parentNode.childNodes && (parentNode.childNodes = []);

                parentNode.childNodes.push(castDraft(node));
                parentNode.hasChildren = true;
                sortChildren(parentNode);
                isPushed = true;
              } else if (!parent) {
                state.nodes.push(castDraft(node));
                sortRoot(state);
                isPushed = true;
              }
            })
          );
          if (node.isActive && isPushed) {
            await afterSelectedNodePush(node);
          }
        },
        unshiftNode: async (node: ITreeNode<T>, parent?: string | ITreeNode<T>) => {
          let isPushed;
          set(
            produce<ITreeStore<T>>(state => {
              const parentNode =
                typeof parent === 'string'
                  ? (getNode(parent, state.nodes) as WritableDraft<ITreeNode<T>>)
                  : (parent as WritableDraft<ITreeNode<T>>);
              if (parentNode) {
                !parentNode.childNodes && (parentNode.childNodes = []);
                parentNode.childNodes.unshift(castDraft(node));
                parentNode.hasChildren = true;
                sortChildren(parentNode);
                isPushed = true;
              } else if (!parent) {
                state.nodes.unshift(castDraft(node));
                sortRoot(state);
                isPushed = true;
              }
            })
          );
          if (node.isActive && isPushed) {
            await afterSelectedNodePush(node);
          }
        },
        reloadNode: async (node: string | ITreeNode<T>, treeNode?: T) => {
          if (!reloadNode) {
            return;
          }

          const oldNode = typeof node === 'string' ? get().actions.findNode(node) : (node as ITreeNode<T>);

          if (!oldNode) {
            return;
          }

          const newNode = await reloadNode(oldNode, treeNode);
          if (!newNode) {
            get().actions.deleteNode(oldNode);
          } else {
            get().actions.updateNode(newNode);
          }
          if (newNode.isActive && !oldNode.isActive) {
            await !afterSelectedNodePush(newNode);
          }
        },
        setChildNodes: async (node: string | ITreeNode<T>, childNodes: ITreeNode<T>[]) => {
          const nodeId = typeof node === 'string' ? node : node.nodeId;
          let currentSelectedNode: ITreeNode<T> | undefined;
          let previousSelectedNode: ITreeNode<T> | undefined;
          set(
            updateNodeIfExist(nodeId, node => {
              previousSelectedNode = (current(node) as ITreeNode<T>).childNodes?.filter(n => n.isActive)[0];
              node.childNodes = castDraft(childNodes);
              currentSelectedNode = childNodes.filter(n => n.isActive)[0];
              sortChildren(node);
              node.isLoading = false;
            })
          );
          if (!!previousSelectedNode?.isActive !== !!currentSelectedNode?.isActive && currentSelectedNode) {
            await !afterSelectedNodePush(currentSelectedNode);
          }
        },
        loadNodes: async (parent: string | ITreeNode<T>, force?: boolean) => {
          const node = typeof parent === 'string' ? get().actions.findNode(parent) : (parent as ITreeNode<T>);
          let nodes = node?.childNodes ?? [];

          if (node && isNeedToLoadNodes(node, force)) {
            force && get().actions.setNodeLoading(node.nodeId, true);
            try {
              nodes = await sortAndLoadNodes(node);
              await get().actions.setChildNodes(node.nodeId, nodes);
            } catch {
              get().actions.setNodeLoading(node.nodeId, false);
            }
          }
          return nodes;
        },
        setNodeLoading: (nodeId: string, isLoading: boolean) =>
          set(updateNodeIfExist(nodeId, node => (node.isLoading = isLoading))),
        loadTree: async () => {
          if (get().nodes.length) {
            set(
              produce<ITreeStore<T>>(state => {
                state.isLoading = true;
                state.nodes = [];
              })
            );
          }
          const nodes = await sortAndLoadNodes();
          set(
            produce<ITreeStore<T>>(state => {
              state.isLoading = false;
              state.nodes = castDraft(nodes);
            })
          );
          await onTreeLoaded?.(get());
        },
        navigateToTheDeep: async (nodeId: string, pathIds: string[]) => {
          let nodes = [] as ITreeNode<T>[];
          if (!pathIds.length) {
            nodes = await get().nodes;
          }
          for (let pathId of pathIds) {
            // we are navigating several levels deep in folder tree, so it is important to load one node after another
            // eslint-disable-next-line no-await-in-loop
            nodes = await get().actions.loadNodes(pathId);
          }

          return nodes.filter(n => n.nodeId === nodeId)[0];
        },
        replaceNodes: nodes => {
          set(
            produce<ITreeStore<T>>(state => {
              state.nodes = nodes as WritableDraft<ITreeNode<T>[]>;
            })
          );
        },
      },
    };
  });

  TreeStoreModelMap.set(treeId, useStore);

  return useStore;
}

function updateNodeIfExist<T>(node: string | ITreeNode<T>, update: (node: WritableDraft<ITreeNode<T>>) => void) {
  return produce<ITreeStore<T>>(state => {
    let updatableNode = getNode(typeof node === 'string' ? node : node.nodeId, state.nodes) as WritableDraft<
      ITreeNode<T>
    >;
    if (updatableNode) {
      update(updatableNode);
    }
  });
}

export function useCreateTreeStore<T>(props: ITreeStoreProps<T>) {
  const useStore = useMemo(() => {
    let store = TreeStoreModelMap.get(props.treeId ?? generateGuid());
    if (store) {
      return store;
    }

    store = createTreeStore(props);

    store.getState().actions.initialize();

    return store;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.treeId]);

  useEffect(() => {
    return () => {
      if (!props.doNotUncache) {
        unCacheStore(props.treeId);
      }
    };
  }, [props.doNotUncache, props.treeId]);

  return useStore;
}

export function getNode<T>(
  id: string,
  nodes: WritableDraft<ITreeNode<T>>[] | ITreeNode<T>[]
): WritableDraft<ITreeNode<T>> | ITreeNode<T> | null {
  let result: WritableDraft<ITreeNode<T>> | ITreeNode<T> | null = null;

  for (const node of nodes) {
    if (node.nodeId === id) {
      result = node;
    } else if (node.childNodes?.length) {
      result = getNode(id, node.childNodes);
    }
    if (result) {
      break;
    }
  }

  return result;
}

export function findNodesList<T>(
  filter: (node: ITreeNode<T> | WritableDraft<ITreeNode<T>>) => boolean,
  nodes: (WritableDraft<ITreeNode<T>> | ITreeNode<T>)[]
): (WritableDraft<ITreeNode<T>> | ITreeNode<T>)[] {
  let result: (ITreeNode<T> | WritableDraft<ITreeNode<T>>)[] = [];

  for (const node of nodes) {
    if (filter(node)) {
      result.push(node);
    }
    if (node.childNodes?.length) {
      result.push(...findNodesList(filter, node.childNodes));
    }
  }

  return result;
}

function getParentCollection<T>(nodeId: string, nodes: WritableDraft<ITreeNode<T>>[]) {
  let result: WritableDraft<ITreeNode<T>>[] = [];

  for (const node of nodes) {
    if (node.nodeId === nodeId) {
      result = nodes;
    } else if (node.childNodes?.length) {
      result = getParentCollection(nodeId, node.childNodes);
    }
    if (result.length) {
      break;
    }
  }

  return result;
}

function setNode<T>(node: ITreeNode<T>, nodes: WritableDraft<ITreeNode<T>>[]) {
  const parentNodes = getParentCollection(node.nodeId, nodes);

  for (let i = 0; i < parentNodes.length; i++) {
    if (parentNodes[i].nodeId === node.nodeId) {
      // parentNodes.splice(i, 1, castDraft(node)); - doesn't work correct, immer doesn't notify about model changed
      // we need remove and push separately
      parentNodes.splice(i, 1);
      parentNodes.splice(i, 0, castDraft(node));
    }
  }
}

function deleteNode<T>(node: ITreeNode<T> | string, nodes: WritableDraft<ITreeNode<T>>[]): boolean {
  const nodeId = typeof node === 'string' ? node : node?.nodeId;
  if (!isNullOrWhitespace(nodeId)) {
    const parentNodes = getParentCollection(nodeId, nodes);

    const index = parentNodes.findIndex(n => n.nodeId === nodeId);
    if (index !== -1) {
      parentNodes.splice(index, 1);
      return true;
    }
  }
  return false;
}
